import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Button,
  TextField,
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  FormHelperText,
  Autocomplete,
  Grid,
  Avatar,
} from '@mui/material';
import { useLead } from '../../contexts/LeadContext';
import { useModal } from '../../contexts/ModalContext';
import AgentSelect from './AgentSelect';
import { fetchAgentSelect, fetchStageStatusFlow } from '../../services/common';

/* ─── helpers ─────────────────────────────────────────── */
const formatLabel = (label) => {
  if (!label) return '';
  return label.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const getStatusColor = (status) => {
  if (!status) return '#94a3b8';
  const s = status.toLowerCase();
  if (['new', 'unassigned', 'assigned', 'reassigned'].includes(s)) return '#3b82f6';
  if (['contacted', 'follow_up_scheduled', 'ringing'].includes(s)) return '#6366f1';
  if (['qualified', 'interested', 'property_shared', 'site_visit_scheduled', 'visited', 'closed_won'].includes(s)) return '#10b981';
  if (['shortlisted', 'offer_made'].includes(s)) return '#f59e0b';
  if (['lost', 'not_interested', 'cold_lead', 'unreachable', 'no_response', 'no_show', 'not_looking_currently'].includes(s)) return '#ef4444';
  return '#94a3b8';
};

const getStageColor = (stage) => {
  if (!stage) return '#94a3b8';
  const s = stage.toLowerCase();
  if (s === 'new') return '#3b82f6';
  if (s === 'contacted') return '#6366f1';
  if (s === 'qualified') return '#f59e0b';
  if (s === 'site_visit') return '#8b5cf6';
  if (s === 'negotiation') return '#f59e0b';
  if (s === 'closure') return '#10b981';
  if (s === 'junk') return '#ef4444';
  return '#94a3b8';
};

/** Only these stages (and their API-defined statuses) appear in the reassignment pickers */
const REASSIGN_ALLOWED_STAGES = ['new', 'qualified', 'negotiation'];

/* ─── tokens ───────────────────────────────────────────── */
const GREEN = '#1a7a4a';
const GREEN_DARK = '#155f39';
const GREEN_LIGHT = '#e8f5ee';
const FONT = '"DM Sans", "Segoe UI", sans-serif';

const styles = {
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '16px',
      overflow: 'hidden',
      fontFamily: FONT,
      boxShadow: '0 24px 60px rgba(0,0,0,0.18)',
      maxWidth: 540,
      width: '100%',
    },
  },
  header: {
    background: 'linear-gradient(135deg, ' + GREEN + ' 0%, ' + GREEN_DARK + ' 100%)',
    color: '#fff',
    px: 3,
    py: 2.5,
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  headerIcon: {
    width: 44,
    height: 44,
    borderRadius: '12px',
    bgcolor: 'rgba(255,255,255,0.18)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 22,
    flexShrink: 0,
  },
  closeBtn: {
    ml: 'auto',
    width: 32,
    height: 32,
    borderRadius: '8px',
    bgcolor: 'rgba(255,255,255,0.18)',
    border: 'none',
    cursor: 'pointer',
    color: '#fff',
    fontSize: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.15s',
    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
  },
  content: { p: 2.5, bgcolor: '#f8fafb' },
  summaryCard: {
    bgcolor: '#fff',
    border: '1.5px solid #e2e8f0',
    borderRadius: '12px',
    p: 2,
    mb: 2,
  },
  sectionTitle: {
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#64748b',
    mb: 1.25,
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
  },
  infoTile: (bg) => ({
    bgcolor: bg,
    borderRadius: '10px',
    p: 1.5,
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
  }),
  tileIcon: (bg) => ({
    width: 34,
    height: 34,
    borderRadius: '8px',
    bgcolor: bg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    flexShrink: 0,
  }),
  currentOwnerStrip: {
    bgcolor: '#fff8ed',
    border: '1.5px solid #fde68a',
    borderRadius: '10px',
    px: 1.75,
    py: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
  },
  agentSection: {
    bgcolor: '#fff',
    border: '1.5px solid #e2e8f0',
    borderRadius: '12px',
    p: 2,
    mb: 2,
  },
  stageStatusSection: {
    bgcolor: '#fff',
    border: '1.5px solid #e2e8f0',
    borderRadius: '12px',
    p: 2,
    mb: 2,
  },
  notesSection: {
    bgcolor: '#fff',
    border: '1.5px solid #e2e8f0',
    borderRadius: '12px',
    p: 2,
    mb: 1,
  },
  statusBadge: (color) => ({
    display: 'inline-flex',
    alignItems: 'center',
    px: 1,
    py: 0.25,
    borderRadius: '6px',
    bgcolor: color + '20',
    color: color,
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.03em',
    border: '1px solid ' + color + '40',
  }),
  cancelBtn: {
    borderRadius: '10px',
    textTransform: 'none',
    fontFamily: FONT,
    fontWeight: 600,
    px: 2.5,
    color: '#ef4444',
    border: '1.5px solid #fecaca',
    bgcolor: '#fff5f5',
    '&:hover': { bgcolor: '#fee2e2', border: '1.5px solid #fca5a5' },
  },
  saveBtn: {
    borderRadius: '10px',
    textTransform: 'none',
    fontFamily: FONT,
    fontWeight: 700,
    px: 2.5,
    bgcolor: GREEN,
    color: '#fff',
    boxShadow: '0 4px 14px ' + GREEN + '55',
    '&:hover': { bgcolor: GREEN_DARK },
    '&.Mui-disabled': { bgcolor: '#a7c4b1', color: '#fff', boxShadow: 'none' },
  },
};

/* ─── shorthand ─────────────────────────────────────────── */
const el = React.createElement;

/* ─── component ─────────────────────────────────────────── */
const ReassignAgent = ({
  open,
  onClose,
  selectedLeadIds = [],
  currentAgent = null,
  currentStage = null,
  currentStatus = null,
  leadName = '',
  leadId = '',
  onComplete = () => {},
}) => {
  const { assignAgents } = useLead();
  const { openSuccessModal, openErrorModal } = useModal();

  const [agents, setAgents] = useState([]);
  const [fetchingAgents, setFetchingAgents] = useState(false);
  const [stageStatusFlow, setStageStatusFlow] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [notes, setNotes] = useState('');
  const [notesError, setNotesError] = useState('');
  const [selectedStage, setSelectedStage] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);
  const [error, setError] = useState(null);
  const [assignmentResult, setAssignmentResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  /** After agents load, pre-select current owner once per dialog open (AgentSelect needs full options list). */
  const didInitialAgentSyncRef = useRef(false);

  const filteredStageFlow = useMemo(() => {
    if (!Array.isArray(stageStatusFlow) || !stageStatusFlow.length) return [];
    const byStage = new Map(stageStatusFlow.map((s) => [s.stage, s]));
    return REASSIGN_ALLOWED_STAGES.map((key) => byStage.get(key)).filter(Boolean);
  }, [stageStatusFlow]);

  const getCurrentStageLabel = useCallback(() => {
    const s = stageStatusFlow.find((s) => s.stage === currentStage);
    return s ? formatLabel(s.label) : formatLabel(currentStage);
  }, [stageStatusFlow, currentStage]);

  const getCurrentStatusLabel = useCallback(() => {
    const s = stageStatusFlow.find((s) => s.stage === currentStage);
    if (!s) return formatLabel(currentStatus);
    const st = s.statuses.find((st) => st.status === currentStatus);
    return st ? st.label : formatLabel(currentStatus);
  }, [stageStatusFlow, currentStage, currentStatus]);

  const getAvailableStages = useCallback(
    () =>
      filteredStageFlow.map((s) => ({
        value: s.stage,
        label: formatLabel(s.label || s.stage),
        order: s.order,
      })),
    [filteredStageFlow]
  );

  const getStatusesForStage = useCallback(
    (stageValue) => {
      const stage = filteredStageFlow.find((s) => s.stage === stageValue);
      if (!stage) return [];
      let rows = stage.statuses;
      if (String(stageValue).toLowerCase() === 'new') {
        rows = rows.filter((st) => String(st.status).toLowerCase() !== 'unassigned');
      }
      return rows.map((st) => ({
        value: st.status,
        label: st.label || formatLabel(st.status),
        order: st.order,
      }));
    },
    [filteredStageFlow]
  );

  useEffect(() => {
    if (!open) return;
    didInitialAgentSyncRef.current = false;
    setNotes(''); setNotesError(''); setAssignmentResult(null); setError(null);
    setSelectedAgent(null);
    const load = async () => {
      setFetchingAgents(true);
      try {
        const [agentsData, flowData] = await Promise.all([fetchAgentSelect(), fetchStageStatusFlow()]);
        setAgents(agentsData || []);
        if (Array.isArray(flowData) && flowData.length) setStageStatusFlow(flowData);
      } catch (e) { console.error(e); } finally { setFetchingAgents(false); }
    };
    load();
  }, [open]);

  useEffect(() => {
    if (!open || fetchingAgents || !agents.length || didInitialAgentSyncRef.current) return;
    didInitialAgentSyncRef.current = true;
    const rawId = currentAgent?.agent_id ?? currentAgent?.id;
    if (rawId == null || rawId === '') {
      setSelectedAgent(null);
      return;
    }
    const match = agents.find(
      (a) =>
        String(a.agent_id ?? a.id ?? '') === String(rawId) ||
        String(a.id ?? '') === String(rawId)
    );
    setSelectedAgent(
      match || {
        id: rawId,
        agent_id: rawId,
        agent_name: currentAgent.name || currentAgent.agent_name || '',
        name: currentAgent.name || currentAgent.agent_name,
      }
    );
  }, [open, fetchingAgents, agents, currentAgent]);

  useEffect(() => {
    if (!open || !filteredStageFlow.length) return;
    const allowed = filteredStageFlow.find((s) => s.stage === currentStage);
    const stageData = allowed || filteredStageFlow[0];
    const stageObj = {
      value: stageData.stage,
      label: formatLabel(stageData.label || stageData.stage),
      order: stageData.order,
    };
    setSelectedStage(stageObj);
    const statuses = getStatusesForStage(stageObj.value);
    setStatusOptions(statuses);
    if (currentStatus) {
      const st = statuses.find((s) => s.value === currentStatus);
      setSelectedStatus(st || statuses[0] || null);
    } else setSelectedStatus(statuses[0] || null);
  }, [open, filteredStageFlow, currentStage, currentStatus, getStatusesForStage]);

  useEffect(() => {
    if (!selectedStage) return;
    const options = getStatusesForStage(selectedStage.value);
    setStatusOptions(options);
    const exists = selectedStatus && options.some((o) => o.value === selectedStatus.value);
    setSelectedStatus(exists ? selectedStatus : options[0] || null);
  }, [selectedStage, selectedStatus, getStatusesForStage]);

  const validateNotes = useCallback(() => {
    setNotesError(''); return true;
  }, []);

  const handleNotesChange = useCallback((e) => {
    const v = e.target.value;
    setNotes(v);
    setNotesError('');
  }, []);

  const handleSubmit = async () => {
    if (!selectedAgent || !selectedLeadIds.length || !selectedStage || !selectedStatus) return;
    if (!validateNotes()) return;
    const notesTrimmed = notes.trim();
    try {
      setIsLoading(true); setAssignmentResult(null); setError(null);
      const result = await assignAgents({
        lead_ids: selectedLeadIds,
        agent_id: selectedAgent.id || selectedAgent.agent_id,
        notes: notesTrimmed,
        new_stage: selectedStage.value,
        new_status: selectedStatus.value,
      });
      if (result && result.errored && result.errored.length > 0 && (!result.passed || !result.passed.length)) {
        setAssignmentResult(result);
        return;
      }
      const agentLabel = selectedAgent.name || selectedAgent.agent_name || 'New owner';
      const stageLabel = selectedStage?.label || selectedStage?.value || '';
      const statusLabel = selectedStatus?.label || selectedStatus?.value || '';
      const pipelineLine =
        stageLabel && statusLabel ? `${stageLabel} · ${statusLabel}` : 'Pipeline updated';

      if (result && result.errored && result.errored.length > 0 && result.passed && result.passed.length > 0) {
        openSuccessModal(
          'Some leads could not be updated',
          `${result.passed.length} lead(s) were reassigned to ${agentLabel} (${pipelineLine}). ${result.errored.length} lead(s) failed — check the list below or try again.`
        );
      } else if (selectedLeadIds.length > 1) {
        openSuccessModal(
          'Lead has been reassigned successfully',
        );
      } else {
        const who = leadName ? `"${leadName}"` : 'This lead';
        openSuccessModal(
          'Lead has been reassigned successfully',
        );
      }
      onComplete(); setSelectedAgent(null); onClose();
    } catch (e) {
      setError(e.message || 'Failed to reassign');
      openErrorModal('Error', e.message || 'Failed to reassign');
    } finally { setIsLoading(false); }
  };

  const handleClose = useCallback(() => {
    setSelectedAgent(null); setAssignmentResult(null); setNotes('');
    setNotesError(''); setError(null); onClose();
  }, [onClose]);

  const submitDisabled = !selectedAgent || isLoading || !selectedLeadIds.length || fetchingAgents || !selectedStage || !selectedStatus;
  const stageColor = getStageColor(currentStage);
  const statusColor = getStatusColor(currentStatus);

  /* ── render helpers ── */

  const renderTileLabel = (text, color) =>
    el(Typography, { sx: { fontSize: '0.65rem', fontWeight: 700, color: color, textTransform: 'uppercase', letterSpacing: '0.06em' } }, text);

  const renderBadge = (label, color) =>
    el(Box, { sx: { ...styles.statusBadge(color), mt: 0.4 } }, label || 'N/A');

  /* ── Lead Details card ── */
  const leadDetailsCard = el(Box, { sx: styles.summaryCard },
    el(Typography, { sx: styles.sectionTitle }, '📋 Lead Details'),

    /* Row 1: Lead ID + Lead Name */
    el(Box, { sx: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mb: 1.25 } },
      el(Box, { sx: styles.infoTile(GREEN_LIGHT) },
        el(Box, { sx: styles.tileIcon('#c6e6d3') }, '🪪'),
        el(Box, { sx: { minWidth: 0 } },
          renderTileLabel('Lead ID', '#4b7a60'),
          el(Typography, { sx: { fontSize: '0.85rem', fontWeight: 700, color: '#1a3d2b', fontFamily: FONT } }, '#' + (leadId || '—'))
        )
      ),
      el(Box, { sx: styles.infoTile('#eef2ff') },
        el(Box, { sx: styles.tileIcon('#dde4ff') },
          el(Avatar, { sx: { width: 24, height: 24, bgcolor: '#4f63d2', fontSize: '0.65rem', fontWeight: 800 } },
            (leadName || 'L').charAt(0).toUpperCase()
          )
        ),
        el(Box, { sx: { minWidth: 0 } },
          renderTileLabel('Lead Name', '#5b6bb0'),
          el(Typography, { sx: { fontSize: '0.85rem', fontWeight: 700, color: '#1e2d6b', fontFamily: FONT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
            leadName || 'Unknown Lead'
          )
        )
      )
    ),

    /* Row 2: Current Stage + Current Status */
    el(Box, { sx: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mb: 1.25 } },
      el(Box, { sx: styles.infoTile('#fdf4ff') },
        el(Box, { sx: styles.tileIcon('#ede9fe') }, '🏷️'),
        el(Box, null,
          renderTileLabel('Current Stage', '#7c3aed'),
          renderBadge(getCurrentStageLabel(), stageColor)
        )
      ),
      el(Box, { sx: styles.infoTile('#fefce8') },
        el(Box, { sx: styles.tileIcon('#fef08a') }, '📊'),
        el(Box, null,
          renderTileLabel('Current Status', '#7a6b1a'),
          renderBadge(getCurrentStatusLabel(), statusColor)
        )
      )
    ),

    /* Row 3: Current Owner strip */
    el(Box, { sx: styles.currentOwnerStrip },
      el(Box, { sx: { fontSize: 18 } }, '👤'),
      el(Box, null,
        el(Typography, { sx: { fontSize: '0.65rem', fontWeight: 700, color: '#92660a', textTransform: 'uppercase', letterSpacing: '0.06em' } }, 'Current Owner'),
        el(Typography, { sx: { fontSize: '0.85rem', fontWeight: 700, color: '#78350f', fontFamily: FONT } },
          (currentAgent && (currentAgent.name || currentAgent.agent_name)) || '—'
        )
      ),
    )
  );

  /* ── New Agent section ── */
  const newAgentSection = el(Box, { sx: styles.agentSection },
    el(Typography, { sx: styles.sectionTitle }, '🧑‍💼 New Owner'),
    fetchingAgents
      ? el(Box, { sx: { display: 'flex', justifyContent: 'center', p: 2 } },
          el(CircularProgress, { size: 22, sx: { color: GREEN } })
        )
      : el(AgentSelect, {
          agents,
          value: selectedAgent ? (selectedAgent.id || selectedAgent.agent_id) : '',
          onChange: (id, agentObj) => setSelectedAgent(agentObj),
          label: 'Select New Agent/Manager',
          name: 'agent',
          required: true,
          error: '',
          size: 'small',
          variant: 'reassign',
          multiple: false,
          showAvatar: true,
          clearable: false,
          sx: {
            '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily: FONT, '&.Mui-focused fieldset': { borderColor: GREEN } },
            '& label.Mui-focused': { color: GREEN },
          },
        })
  );

  /* ── Stage & Status section ── */
  const stageStatusSection = el(Box, { sx: styles.stageStatusSection },
    el(Typography, { sx: styles.sectionTitle }, '📍 Stage & Status'),
    el(Grid, { container: true, spacing: 1.5 },
      el(Grid, { item: true, xs: 12, sm: 6 },
        el(Autocomplete, {
          value: selectedStage,
          onChange: (_, v) => { setSelectedStage(v); setSelectedStatus(null); },
          options: getAvailableStages(),
          getOptionLabel: (o) => o.label,
          disableClearable: true,
          disabled: !getAvailableStages().length,
          renderInput: (params) =>
            el(TextField, {
              ...params,
              label: 'Stage',
              required: true,
              size: 'small',
              sx: {
                '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily: FONT, '&.Mui-focused fieldset': { borderColor: GREEN } },
                '& label.Mui-focused': { color: GREEN },
              },
            }),
        })
      ),
      el(Grid, { item: true, xs: 12, sm: 6 },
        el(Autocomplete, {
          value: selectedStatus,
          onChange: (_, v) => setSelectedStatus(v),
          options: statusOptions,
          getOptionLabel: (o) => o.label,
          disableClearable: true,
          disabled: !selectedStage || !statusOptions.length,
          renderInput: (params) =>
            el(TextField, {
              ...params,
              label: 'Status',
              required: true,
              size: 'small',
              sx: {
                '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily: FONT, '&.Mui-focused fieldset': { borderColor: GREEN } },
                '& label.Mui-focused': { color: GREEN },
              },
            }),
        })
      )
    )
  );

  /* ── Notes section ── */
  const notesSection = el(Box, { sx: styles.notesSection },
    el(Typography, { sx: styles.sectionTitle }, '📝 Notes (Optional)'),
    el(TextField, {
      fullWidth: true,
      multiline: true,
      rows: 4,
      placeholder: 'Explain the reason for reassignment and any handover context for the new agent…',
      value: notes,
      onChange: handleNotesChange,
      onBlur: validateNotes,
      error: !!notesError,
      size: 'small',
      sx: {
        '& .MuiOutlinedInput-root': {
          borderRadius: '10px',
          fontFamily: FONT,
          fontSize: '0.85rem',
          '&.Mui-focused fieldset': { borderColor: GREEN },
          '&.Mui-error fieldset': { borderColor: '#ef4444' },
        },
        '& label.Mui-focused': { color: GREEN },
      },
    }),
    notesError
      ? el(FormHelperText, { error: true, sx: { m: 0, mt: 0.75, fontSize: '0.72rem' } }, notesError)
      : null
  );

  /* ── Error banners ── */
  const errorBanners = [
    assignmentResult && assignmentResult.errored && assignmentResult.errored.length > 0 &&
      el(Box, { sx: { bgcolor: '#fff5f5', border: '1.5px solid #fecaca', borderRadius: '10px', p: 1.5, mb: 1 } },
        el(Typography, { sx: { color: '#b91c1c', fontSize: '0.8rem', fontWeight: 700 } },
          '⚠️ Failed for ' + assignmentResult.errored.length + ' lead(s)'
        ),
        el(Box, { sx: { mt: 0.75, maxHeight: 80, overflow: 'auto' } },
          ...assignmentResult.errored.map((item) =>
            el(Typography, { key: item.id, sx: { fontSize: '0.72rem', color: '#b91c1c' } },
              'Lead #' + item.id + ': ' + (item.message || 'Unknown error')
            )
          )
        )
      ),
    error &&
      el(Typography, { sx: { color: '#b91c1c', fontSize: '0.75rem', mt: 0.5, bgcolor: '#fff5f5', borderRadius: '8px', px: 1.5, py: 0.75, border: '1px solid #fecaca' } },
        '⚠️ ' + error
      ),
  ].filter(Boolean);

  /* ── main render ── */
  return el(Dialog, { open, onClose: handleClose, sx: styles.dialog, maxWidth: 'sm', fullWidth: true },

    /* Header */
    el(Box, { sx: styles.header },
      el(Box, null,
        el(Typography, { sx: { fontFamily: FONT, fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.2, color: '#fff' } }, 'Reassign Agent'),
      ),
      el(Box, { component: 'button', sx: styles.closeBtn, onClick: handleClose }, '✕')
    ),

    /* Content */
    el(DialogContent, { sx: styles.content },
      leadDetailsCard,
      newAgentSection,
      stageStatusSection,
      notesSection,
      ...errorBanners
    ),

    /* Footer */
    el(DialogActions, { sx: { px: 2.5, py: 2, bgcolor: '#f8fafb', borderTop: '1px solid #e2e8f0', gap: 1 } },
      el(Button, { onClick: handleClose, disabled: isLoading, sx: styles.cancelBtn, variant: 'outlined' }, '✕ Cancel'),
      el(Button, { variant: 'contained', onClick: handleSubmit, disabled: submitDisabled, sx: styles.saveBtn },
        isLoading
          ? el(React.Fragment, null,
              el(CircularProgress, { size: 14, sx: { color: '#fff', mr: 1 } }),
              'Saving…'
            )
          : '✅ Save Reassignment'
      )
    )
  );
};

export default ReassignAgent;